import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
/**
 * STORAGE OFICIAL DE DOCUMENTOS (Bloco 03) — porta única de armazenamento de
 * binário. Ninguém fora deste serviço grava ou lê o dado bruto; o resto do
 * sistema só conhece `storageRef` (opaco) e chama `resolveDownloadUrl`, nunca
 * um caminho físico (`/data/files/...`) direto.
 */
export interface DocumentStorageService {
  /** Guarda o binário (dataURL/base64) e devolve uma referência opaca. */
  store(content: string): Promise<string>;
  /** Resolve a referência para algo que a UI pode abrir (VISUALIZAR/BAIXAR) — autorizado por quem chama, nunca exposto como caminho de storage. */
  resolveDownloadUrl(storageRef: string): Promise<string | null>;
  remove(storageRef: string): Promise<void>;
}

/**
 * Implementação SÓ em memória — testes e ambientes sem armazenamento durável.
 *
 * Antes ela espelhava TODOS os arquivos numa única chave do localStorage
 * ("inspect_document_blobs"). Com fotos, assinaturas e logos a cota (~5 MB)
 * estourava, a gravação falhava em silêncio e o arquivo existia só na memória
 * da aba: o preview do Studio mostrava o logo/fundo do login e, depois de
 * sair ou recarregar, o /login não o encontrava mais. No navegador o sistema
 * usa `DurableDocumentStorageService` (IndexedDB, abaixo).
 */
export class InMemoryDocumentStorageService implements DocumentStorageService {
  private blobs = new Map<string, string>();

  async store(content: string): Promise<string> {
    const ref = `doc-storage:${globalThis.crypto.randomUUID()}`;
    this.blobs.set(ref, content);
    return ref;
  }

  async resolveDownloadUrl(storageRef: string): Promise<string | null> {
    return this.blobs.get(storageRef) ?? null;
  }

  async remove(storageRef: string): Promise<void> {
    this.blobs.delete(storageRef);
  }
}

/** Onde os arquivos ficam de fato (IndexedDB no navegador; um fake nos testes). */
export interface BlobPersistence {
  get(ref: string): Promise<string | null>;
  put(ref: string, content: string): Promise<void>;
  putMany(entries: ReadonlyArray<readonly [string, string]>): Promise<void>;
  delete(ref: string): Promise<void>;
}

/** Arquivos gravados pelo formato antigo (uma chave única do localStorage). */
export interface LegacyBlobSource {
  read(): Array<[string, string]> | null;
  clear(): void;
}

export const LEGACY_BLOBS_KEY = "inspect_document_blobs";

export function localStorageLegacyBlobs(): LegacyBlobSource {
  return {
    read: () => {
      try {
        if (typeof localStorage === "undefined") return null;
        const raw = localStorage.getItem(LEGACY_BLOBS_KEY);
        return raw ? (JSON.parse(raw) as Array<[string, string]>) : null;
      } catch {
        return null;
      }
    },
    clear: () => { try { localStorage.removeItem(LEGACY_BLOBS_KEY); } catch { /* nada a liberar */ } }
  };
}

/**
 * ARMAZENAMENTO DURÁVEL de arquivos (modo navegador).
 *
 *  - `store` só devolve a referência DEPOIS de gravar. Se a gravação falhar
 *    (espaço, bloqueio do navegador), o envio é RECUSADO com o motivo — nunca
 *    mais uma referência que aponta para um arquivo que não existe.
 *  - Na primeira abertura, os arquivos do formato antigo são copiados para cá
 *    e só então a chave antiga é removida (libera a cota do localStorage).
 *    Falhou a cópia? A chave antiga fica, e nada se perde.
 */
export class DurableDocumentStorageService implements DocumentStorageService {
  private readonly cache = new Map<string, string>();
  private readonly ready: Promise<void>;

  constructor(private readonly persistence: BlobPersistence, legacy: LegacyBlobSource | null = null) {
    this.ready = this.migrate(legacy);
  }

  private async migrate(legacy: LegacyBlobSource | null): Promise<void> {
    const entries = legacy?.read();
    if (!entries || entries.length === 0) return;
    for (const [ref, content] of entries) this.cache.set(ref, content);
    try {
      await this.persistence.putMany(entries);
      legacy!.clear();
    } catch (e) {
      console.warn("[ARQUIVOS] Não foi possível migrar os arquivos antigos para o armazenamento durável; eles continuam no formato antigo.", e);
    }
  }

  async store(content: string): Promise<string> {
    await this.ready;
    const ref = `doc-storage:${globalThis.crypto.randomUUID()}`;
    try {
      await this.persistence.put(ref, content);
    } catch (e) {
      console.error("[ARQUIVOS] Falha ao gravar arquivo", e);
      throw new AppError(ErrorCode.Validation, "Blob persistence failed", {
        safeMessage: "Não foi possível gravar o arquivo neste navegador (espaço de armazenamento insuficiente ou bloqueado). Nada foi salvo — libere espaço ou use o ambiente com servidor."
      });
    }
    this.cache.set(ref, content);
    return ref;
  }

  async resolveDownloadUrl(storageRef: string): Promise<string | null> {
    await this.ready;
    const hit = this.cache.get(storageRef);
    if (hit !== undefined) return hit;
    const content = await this.persistence.get(storageRef);
    if (content !== null) this.cache.set(storageRef, content);
    return content;
  }

  async remove(storageRef: string): Promise<void> {
    await this.ready;
    await this.persistence.delete(storageRef);
    this.cache.delete(storageRef);
  }
}

/** SHA-256 do conteúdo (mesma técnica já usada para hash de Minuta/AVR neste projeto) — parte do versionamento exigido (documentId/revision/hash). */
export async function sha256Hex(content: string): Promise<string> {
  const bytes = new TextEncoder().encode(content);
  const digest = await globalThis.crypto.subtle.digest("SHA-256", bytes);
  return Array.from(new Uint8Array(digest)).map(b => b.toString(16).padStart(2, "0")).join("");
}
